import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  createDependencyLink,
  deleteDependencyLink,
  listLinks,
  type DependencyLink,
  type LinksView,
} from '../api.js';
import { CrossLink } from './cross-links.js';

/**
 * The dependency panel for one record: what it waits on, what waits on it.
 *
 * TWO DECISIONS FROM `06` LIVE HERE
 * ---------------------------------
 *   * A cycle is a WARNING, not a refusal — "a user creating a circular
 *     dependency should be told, not blocked mid-task". The loop comes back
 *     named and this panel says it out loud, permanently until it is broken.
 *   * This is NOT a critical path. No float, no levelling, no rescheduling —
 *     the chain is shown and a human decides. Saying so is part of the design.
 *
 * DELIVERS-TO IS THE SECOND KIND OF LINK, AND IT IS NOT A DEPENDENCY
 * -----------------------------------------------------------------
 * `06`'s legal matrix has `activity → milestone: delivers_to` in it, and
 * migration `0026` enforces it in a CHECK. It is what connects the work on site
 * to the dates the project is judged on — the user asked for exactly this
 * ("milestone↔activity") — and it is a different sentence from "waits on":
 * nothing is blocked, the milestone is simply what this work is FOR. So it gets
 * its own control and its own line, rather than being folded into "holds up",
 * which would read as a threat instead of a purpose.
 */

interface LinkTarget {
  readonly id: string;
  readonly label: string;
}

interface Props {
  readonly projectId: string;
  readonly entityType: 'task' | 'activity';
  readonly entityId: string;
  /** Other records of the same kind this one could wait on: id + name. */
  readonly candidates: readonly LinkTarget[];
  /**
   * Milestones this record can be declared to deliver. Only activities have
   * them — `06`'s matrix has no task → milestone edge, so the control is absent
   * for a task rather than offering a link the database would refuse.
   */
  readonly milestones?: readonly LinkTarget[];
  readonly canEdit: boolean;
  /** A new predecessor may block this record immediately (rule P4). */
  readonly onChanged: () => void;
}

export function Dependencies({
  projectId,
  entityType,
  entityId,
  candidates,
  milestones = [],
  canEdit,
  onChanged,
}: Props): JSX.Element {
  const [links, setLinks] = useState<readonly DependencyLink[] | null>(null);
  const [cycle, setCycle] = useState<LinksView['cycle']>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [choosing, setChoosing] = useState('');
  const [delivering, setDelivering] = useState('');

  const load = useCallback(async () => {
    try {
      setLinks(await listLinks(projectId, entityType, entityId));
    } catch {
      // A denied list is not worth a banner on a record whose own screen has
      // already said what the reader may see.
      setLinks([]);
    }
  }, [projectId, entityType, entityId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!links) return <p className="hint">Loading dependencies…</p>;

  const waitsOn = links.filter(
    (l) => l.to_type === entityType && l.to_id === entityId && l.link_type === 'blocks',
  );
  const blocks = links.filter(
    (l) => l.from_type === entityType && l.from_id === entityId && l.link_type === 'blocks',
  );
  const delivers = links.filter(
    (l) => l.from_id === entityId && l.to_type === 'milestone' && l.link_type === 'delivers_to',
  );
  const linkedIds = new Set(waitsOn.map((l) => l.from_id).concat(blocks.map((l) => l.to_id)));
  const options = candidates.filter((c) => c.id !== entityId && !linkedIds.has(c.id));
  const deliveredIds = new Set(delivers.map((l) => l.to_id));
  const milestoneOptions = milestones.filter((m) => !deliveredIds.has(m.id));

  async function add(predecessorId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const view = await createDependencyLink(projectId, {
        from_type: entityType,
        from_id: predecessorId,
        to_type: entityType,
        to_id: entityId,
        link_type: 'blocks',
      });
      setLinks(view.links);
      setCycle(view.cycle);
      setChoosing('');
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  /** Declare that this activity delivers a milestone (`06`'s `delivers_to`). */
  async function deliver(milestoneId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const view = await createDependencyLink(projectId, {
        from_type: entityType,
        from_id: entityId,
        to_type: 'milestone',
        to_id: milestoneId,
        link_type: 'delivers_to',
      });
      setLinks(view.links);
      setCycle(view.cycle);
      setDelivering('');
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(linkId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteDependencyLink(projectId, linkId);
      setCycle(null);
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dependencies">
      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {/* Told, not blocked (`06`). The link saved; the loop is the news. */}
      {cycle && (
        <div className="alert" role="alert">
          <span>
            <strong>These now depend on each other in a circle:</strong>{' '}
            {cycle.map((n) => n.label ?? n.id).join(' → ')}. Nothing in the loop can start until
            the loop is broken — remove one of its links.
          </span>
        </div>
      )}

      {waitsOn.length === 0 && blocks.length === 0 && !canEdit && (
        <p className="hint">No dependencies.</p>
      )}

      {waitsOn.length > 0 && (
        <p className="hint">
          <strong>Waits on:</strong>{' '}
          {waitsOn.map((link, index) => (
            <span key={link.id}>
              {index > 0 && ', '}
              {link.from_label ?? link.from_id}
              {canEdit && (
                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => void remove(link.id)}
                >
                  Unlink
                  <span className="visually-hidden">
                    {' '}
                    from {link.from_label ?? 'its predecessor'}
                  </span>
                </button>
              )}
            </span>
          ))}
        </p>
      )}

      {blocks.length > 0 && (
        <p className="hint">
          <strong>Holds up:</strong> {blocks.map((l) => l.to_label ?? l.to_id).join(', ')}
        </p>
      )}

      {/* The date this work is FOR. A link, because the milestone lives on the
          Overview and the whole point is to be able to get to it. */}
      {delivers.length > 0 && (
        <p className="hint">
          <strong>Delivers:</strong>{' '}
          {delivers.map((link, index) => (
            <span key={link.id}>
              {index > 0 && ', '}
              <CrossLink
                projectId={projectId}
                section="overview"
                focus={link.to_id}
                says="the milestone this work delivers"
              >
                {link.to_label ?? link.to_id}
              </CrossLink>
              {canEdit && (
                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => void remove(link.id)}
                >
                  Unlink
                  <span className="visually-hidden"> from {link.to_label ?? 'the milestone'}</span>
                </button>
              )}
            </span>
          ))}
        </p>
      )}

      {canEdit && options.length > 0 && (
        <div className="field-row">
          <div className="field">
            <label htmlFor={`dep-${entityId}`}>Waits on…</label>
            <select
              id={`dep-${entityId}`}
              value={choosing}
              disabled={busy}
              onChange={(e) => setChoosing(e.target.value)}
            >
              <option value="">Choose…</option>
              {options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="inline"
            disabled={busy || !choosing}
            onClick={() => void add(choosing)}
          >
            Add dependency
          </button>
        </div>
      )}

      {canEdit && milestoneOptions.length > 0 && (
        <div className="field-row">
          <div className="field">
            <label htmlFor={`del-${entityId}`}>Delivers milestone…</label>
            <select
              id={`del-${entityId}`}
              value={delivering}
              disabled={busy}
              onChange={(e) => setDelivering(e.target.value)}
            >
              <option value="">Choose…</option>
              {milestoneOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="inline"
            disabled={busy || !delivering}
            onClick={() => void deliver(delivering)}
          >
            Link to milestone
          </button>
        </div>
      )}
    </div>
  );
}
